import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AiPromptSummaryResponse,
  CategoryConfigurationDefinitionResponse,
  EntityListResponse,
  PackingPictureResponse,
  StoreResponse,
  StoreScheduleResponse,
} from '@ydistri/api-sdk';
import { CalculationActionType, CalculationTableParams } from '../Calculations/calculationsTypes';
import { EditRedistributionData } from '../CalculationDetail/Editation/editRedistributionLib';
import type { EntityListTypeSupported } from '../ProjectAdministration/EntityListAdministration/entityListsLib';

export enum AvailableModals {
  CATEGORY_INSIGHTS = 'categoryInsights',
  CATEGORY_EXCEPTIONS = 'categoryExceptions',
  ENTITY_LIST_CONTENTS = 'entityListContents',
  ENTITY_LIST_SKU_HANDLING = 'entityListSkuHandling',
  ENTITY_LIST_UNARCHIVE_CONFIRMATION = 'entityListUnarchiveConfirmation',
  ENTITY_LIST_CONFIG_DELETE = 'entityListConfigDelete',
  REMOVABLE_CONFIG_DELETE = 'removableConfigDelete',
  NEW_APP_VERSION = 'newAppVersion',
  REPORT_ERROR = 'reportError',
  SCOPE_EDIT = 'scopeEdit',
  SCOPE_ATTRIBUTES_EDIT = 'scopeAttributesEdit',
  STORE_OVERVIEW = 'storeOverview',
  SET_PRODUCTION = 'setProduction',
  UNSET_PRODUCTION = 'unsetProduction',
  MULTI_CALCULCATION_ACTIONS = 'multiCalculationActions',
  STORE_SALE_INFO = 'storeSaleInfo',
  SKU_DAILY_SALES = 'skuDailySales',
  PICKING_LINE_PRODUCT_GROUPS = 'pickingLineProductGroups',
  BOX_PACKING_PICTURES = 'boxPackingPictures',
  EDIT_REDISTRIBUTION = 'editRedistribution',
  SKU_REDISTRIBUTION_CHANGELOG = 'skuRedistributionChangelog',
  STORE_SCHEDULE_EDIT = 'storeScheduleEdit',
  STORE_SCHEDULE_DELETE = 'storeScheduleDelete',
  AI_PROMPT_EDIT = 'aiPromptEdit',
  AI_PROMPT_DELETE = 'aiPromptDelete',
  AI_PROMPT_HISTORY = 'aiPromptHistory',
}

export interface ModalDataMap {
  [AvailableModals.ENTITY_LIST_UNARCHIVE_CONFIRMATION]: {
    entityListId: number;
    entityListType: EntityListTypeSupported;
    projectShortName: string;
  };
  [AvailableModals.ENTITY_LIST_SKU_HANDLING]: {
    entityListId: number;
    entityListType: EntityListTypeSupported;
    verificationTimeStamp: string;
  };
  [AvailableModals.CATEGORY_INSIGHTS]: undefined;
  [AvailableModals.CATEGORY_EXCEPTIONS]: undefined;
  [AvailableModals.ENTITY_LIST_CONFIG_DELETE]: {
    entityList: EntityListResponse;
    configurationDefinition: CategoryConfigurationDefinitionResponse;
  };
  [AvailableModals.REMOVABLE_CONFIG_DELETE]: {
    configurationDefinition: CategoryConfigurationDefinitionResponse;
  };
  [AvailableModals.ENTITY_LIST_CONTENTS]: {
    entityList: EntityListResponse;
  };
  [AvailableModals.NEW_APP_VERSION]: undefined;
  [AvailableModals.REPORT_ERROR]: undefined;
  [AvailableModals.SCOPE_EDIT]: {
    scopeId: number;
  };
  [AvailableModals.SCOPE_ATTRIBUTES_EDIT]: {
    scopeId: number;
  };
  [AvailableModals.STORE_OVERVIEW]: undefined;
  [AvailableModals.SET_PRODUCTION]: {
    calculationId: number;
    isErpDownload: boolean;
  };
  [AvailableModals.UNSET_PRODUCTION]: {
    calculationId: number;
  };
  [AvailableModals.MULTI_CALCULCATION_ACTIONS]: {
    calculationIds: number[];
    calculationTableParams: CalculationTableParams;
    calculationActionType: CalculationActionType;
  };
  [AvailableModals.STORE_SALE_INFO]: undefined;
  [AvailableModals.SKU_DAILY_SALES]: {
    skuId: number;
  };
  [AvailableModals.PICKING_LINE_PRODUCT_GROUPS]: {
    sourceSkuId: number;
    targetSkuId: number;
    sourceStore: StoreResponse;
    targetStore: StoreResponse;
  };
  [AvailableModals.EDIT_REDISTRIBUTION]: EditRedistributionData;
  [AvailableModals.SKU_REDISTRIBUTION_CHANGELOG]: {
    calculationId: number;
  };
  [AvailableModals.STORE_SCHEDULE_EDIT]: {
    /** Absent when creating. */
    storeSchedule?: StoreScheduleResponse;
  };
  [AvailableModals.STORE_SCHEDULE_DELETE]: {
    storeSchedule: StoreScheduleResponse;
  };
  [AvailableModals.AI_PROMPT_EDIT]: {
    /** Absent when creating. */
    aiPrompt?: AiPromptSummaryResponse;
  };
  [AvailableModals.AI_PROMPT_DELETE]: {
    aiPrompt: AiPromptSummaryResponse;
  };
  [AvailableModals.AI_PROMPT_HISTORY]: {
    aiPrompt: AiPromptSummaryResponse;
  };
  [AvailableModals.BOX_PACKING_PICTURES]: {
    calculationId: number;
    sourceStoreId: number;
    targetStoreId: number;
    departmentId?: number;
    boxes: PackingPictureResponse[];
  };
}

interface ModalState {
  openedModal: AvailableModals | undefined;
  modalData: Partial<ModalDataMap>;
}

const initialState: ModalState = {
  openedModal: undefined,
  modalData: {},
};

export interface OpenModalPayload<T extends AvailableModals> {
  type: T;
  data: ModalDataMap[T];
}

export const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    openModal: <T extends AvailableModals>(
      state: ModalState,
      action: PayloadAction<OpenModalPayload<T>>,
    ) => {
      // eslint-disable-next-line no-console -- we output here for reference
      console.log(action.payload);
      state.openedModal = action.payload.type;
      state.modalData[action.payload.type] = action.payload.data;
    },
    closeModal: state => {
      state.openedModal = undefined;
      state.modalData = {};
    },
    resetState: () => initialState,
  },
});

export const { openModal, closeModal } = modalSlice.actions;

export const modalReducer = modalSlice.reducer;
